import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Star, Shield, Zap, Users, Gift } from "lucide-react";
import { openIntercomForSales } from "@/utils/intercom";
import { useAuthForm } from "@/contexts/AuthFormProvider";
import {
  apiGetSubscriptions,
  ProductsResponse
} from '@/services/ProductSubscriptionService';

interface PricingPlan {
  name: string;
  tagline: string;
  description: string;
  price: string;
  period: string;
  credits: string;
  popular?: boolean;
  features: string[];
  advantages: string[];
  icon: React.ReactNode;
  buttonText: string;
}

const PricingSection: React.FC = () => {
  const { showRegisterForm } = useAuthForm();
  const [productsResponse, setProductsResponse] = useState<ProductsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await apiGetSubscriptions<ProductsResponse>();
      if (response) {
        setProductsResponse(response.data);
      }
    } catch (err: any) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount / 100);
  };

  const getIconForPlan = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes('startup') || name.includes('starter')) return <Zap className="w-6 h-6" />;
    if (name.includes('business')) return <Users className="w-6 h-6" />;
    if (name.includes('enterprise')) return <Shield className="w-6 h-6" />;
    if (name.includes('discovery')) return <Star className="w-6 h-6" />;
    if (name.includes('creator')) return <Zap className="w-6 h-6" />;
    if (name.includes('developer')) return <Users className="w-6 h-6" />;
    return <Star className="w-6 h-6" />;
  };

  const getTaglineForPlan = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes('startup') || name.includes('starter')) return "Build & Scale Fast";
    if (name.includes('business')) return "Enterprise Power";
    if (name.includes('enterprise')) return "Ultimate Scale";
    if (name.includes('discovery')) return "Try It. Love It.";
    if (name.includes('creator')) return "Build a Few, Learn a Lot.";
    if (name.includes('developer')) return "Scale Your Code.";
    return "Get Started";
  };

  const getAdvantagesForPlan = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes('discovery')) {
      return [
        "Risk-free trial: see real results before you buy more",
        "Fast onboarding: onboard in minutes"
      ];
    }
    if (name.includes('creator') || name.includes('startup') || name.includes('starter')) {
      return [
        "Affordable ramp-up—test multiple use-cases",
        "Faster feedback loops: get quickly a working service",
        "Explore all the power of ShredsAI"
      ];
    }
    if (name.includes('developer') || name.includes('business')) {
      return [
        "Perfect to start working on a full project",
        "High-velocity dev cycles with fastest servers",
        "Quick support to resolve any issue"
      ];
    }
    return [
      "Build production-ready code",
      "Fast turnaround times"
    ];
  };

  const getDescriptionForPlan = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes('discovery')) return "Perfect for evaluating code and architecture quality before scaling up.";
    if (name.includes('creator') || name.includes('startup') || name.includes('starter')) return "Get your first Production-Ready Microservice in a few hours.";
    if (name.includes('developer')) return "Start a full project and get quickly a first working version.";
    if (name.includes('business')) return "Perfect for business teams and projects.";
    return `Perfect for ${planName.toLowerCase()} teams and projects.`;
  };

  const staticPlans: PricingPlan[] = [
    {
      name: "Free Forever",
      tagline: "Get Started for Free",
      description: "Perfect for trying out ShredsAI and small projects without any cost.",
      price: "$0",
      period: "/forever",
      credits: "1 Project",
      icon: <Gift className="w-6 h-6" />,
      buttonText: "Start Free",
      features: [
        "1 Project",
        "Full architecture generation",
        "Detailed software specifications",
        "Complete documentation"
      ],
      advantages: [
        "100% Free",
        "Perfect for evaluation and learning"
      ]
    }
  ];

  // Get monthly products from API
  const apiProducts = productsResponse?.products || [];
  const monthlyProducts = apiProducts
    .filter(p => p.interval?.toLowerCase().includes('month'))
    .sort((a, b) => a.amount - b.amount);

  // Transform API products into PricingPlan format
  const apiPlans: PricingPlan[] = monthlyProducts.map((product) => ({
    name: product.name,
    tagline: getTaglineForPlan(product.name),
    description: getDescriptionForPlan(product.name),
    price: formatCurrency(product.amount, product.currency),
    period: "/month",
    credits: `${product.credits.toLocaleString()} credits`,
    popular: product.popular,
    icon: getIconForPlan(product.name),
    buttonText: "Choose Plan",
    features: [
      `${product.credits.toLocaleString()} credits`,
      "Priority queue processing",
      "All code-generation models",
      "Model improvements",
      "Email support"
    ],
    advantages: getAdvantagesForPlan(product.name)
  }));

  // Combine static free plan with API plans
  const mainPlans: PricingPlan[] = [...staticPlans, ...apiPlans];

  return (
    <div id="pricing" className="relative py-12 md:py-16 overflow-hidden bg-gradient-to-br from-slate-900/50 via-gray-900/80 to-slate-800/50">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/6 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/6 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gradient-to-r from-cyan-500/3 via-blue-500/5 to-purple-500/3 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-10%" }}
        >
          <motion.h2 
            className="text-5xl lg:text-6xl font-black text-white leading-tight mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Simple, Transparent
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Pricing
            </span>
          </motion.h2>
          
          <motion.p 
            className="text-lg text-gray-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            Choose the perfect plan for your development needs
          </motion.p>
        </motion.div>

        {/* Pricing Cards */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-cyan-500"></div>
              <div className="absolute inset-0 animate-ping rounded-full border-2 border-purple-500 opacity-20"></div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 items-stretch">
            {mainPlans.map((plan, index) => (
            <motion.div
              key={index}
              className={`relative group ${plan.popular ? 'xl:scale-105' : ''}`}
              style={{ isolation: 'isolate' }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true, margin: "-5%" }}
            >
              {plan.popular && (
                <div
                  className="absolute -top-4 left-1/2 transform -translate-x-1/2"
                  style={{ zIndex: 9999 }}
                >
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                    Most Popular
                  </div>
                </div>
              )}

              <div className={`relative h-full bg-gray-900/40 backdrop-blur-sm border rounded-3xl overflow-visible group-hover:bg-gray-800/60 transition-all duration-300 ${
                plan.popular ? 'border-blue-400/50 shadow-lg shadow-blue-500/20' : 
                plan.name === 'Free Forever' ? 'border-green-400/50 shadow-lg shadow-green-500/20' : 
                'border-gray-700/50'
              }`}>
                <motion.div 
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-blue-500/10 to-purple-500/10' 
                      : plan.name === 'Free Forever'
                        ? 'bg-gradient-to-r from-green-500/10 to-blue-500/10'
                        : 'bg-gradient-to-r from-blue-500/5 to-purple-500/5'
                  }`}
                />
                
                <div className="relative z-10 p-8 h-full flex flex-col">
                  {/* Icon & Name */}
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                      plan.popular ? 'bg-blue-500/20 text-blue-400' : 
                      plan.name === 'Free Forever' ? 'bg-green-500/20 text-green-400' :
                      'bg-gray-700/50 text-gray-400'
                    }`}>
                      {plan.icon}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                      <p className={`text-sm ${
                        plan.popular ? 'text-blue-300' : 
                        plan.name === 'Free Forever' ? 'text-green-300' : 
                        'text-gray-400'
                      }`}>
                        {plan.tagline}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {plan.description}
                  </p>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-black text-white">{plan.price}</span>
                      <span className="text-gray-400 ml-2">{plan.period}</span>
                    </div>
                    {plan.name !== "Enterprise" && (
                      <p className="text-sm text-gray-400 mt-2">
                        Auto-renewable • Cancel anytime
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <Check className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${
                            plan.popular ? 'text-blue-400' :
                            plan.name === 'Free Forever' ? 'text-green-400' :
                            'text-green-400'
                          }`} />
                          <span className="text-gray-300 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Advantages */}
                  <div className="flex-grow mb-8">
                    <h4 className="text-white font-semibold mb-3">Key Advantages:</h4>
                    <ul className="space-y-2">
                      {plan.advantages.map((advantage, idx) => (
                        <li key={idx} className="flex items-start">
                          <div className={`w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0 ${
                            plan.popular ? 'bg-blue-400' : 
                            plan.name === 'Free Forever' ? 'bg-green-400' :
                            'bg-purple-400'
                          }`} />
                          <span className="text-gray-300 text-sm">{advantage}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Button - Always at bottom */}
                  <motion.button
                    className={`w-full py-4 px-6 rounded-2xl font-semibold transition-all duration-300 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500 shadow-lg shadow-blue-500/25'
                        : plan.name === 'Free Forever'
                          ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white hover:from-green-500 hover:to-blue-500 shadow-lg shadow-green-500/25'
                          : 'bg-gray-800 text-white hover:bg-gray-700 border border-gray-600'
                    }`}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (plan.buttonText === "Contact Sales") {
                        openIntercomForSales();
                      } else {
                        window.lintrk('track', { conversion_id: 23344122 });
                        showRegisterForm();
                      }
                    }}
                  >
                    {plan.buttonText}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
          </div>
        )}

        {/* Enterprise Plan - Horizontal Layout */}
        <motion.div
          className="mt-16 mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="relative group bg-gradient-to-r from-gray-900/60 via-gray-800/80 to-gray-900/60 backdrop-blur-sm border border-gray-700/50 rounded-3xl overflow-hidden hover:border-blue-400/50 transition-all duration-300">
            <motion.div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-blue-500/5 to-purple-500/5"
            />
            
            <div className="relative z-10 p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-center">
                {/* Left Section - Enterprise Info */}
                <div className="lg:col-span-2">
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center mr-6">
                      <Shield className="w-8 h-8 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-3xl lg:text-4xl font-black text-white mb-2">Enterprise</h3>
                      <p className="text-xl text-blue-300 font-semibold">Maximum Security & Control</p>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                    Deploy ShredsAI on-premise in your own servers, completely offline for maximum security and full compliance control.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-white font-semibold mb-4">Core Features:</h4>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <Check className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0 text-blue-400" />
                          <span className="text-gray-300 text-sm">On-premise deployment</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0 text-blue-400" />
                          <span className="text-gray-300 text-sm">Completely offline operation</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0 text-blue-400" />
                          <span className="text-gray-300 text-sm">Unlimited credits</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-4">Enterprise Benefits:</h4>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <div className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0 bg-blue-400" />
                          <span className="text-gray-300 text-sm">Maximum security with no data leaving your infrastructure</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0 bg-blue-400" />
                          <span className="text-gray-300 text-sm">Full compliance control for regulated industries</span>
                        </li>
                        <li className="flex items-start">
                          <div className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0 bg-blue-400" />
                          <span className="text-gray-300 text-sm">Custom integrations with your existing systems</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Right Section - Pricing & CTA */}
                <div className="text-center lg:text-right">
                  <div className="mb-6">
                    <div className="text-5xl font-black text-white mb-2">Custom</div>
                    <div className="text-xl text-gray-400 mb-4">pricing</div>
                    <div className="inline-block px-4 py-2 bg-blue-500/20 border border-blue-400/30 rounded-full">
                      <span className="text-blue-300 font-semibold text-sm">Unlimited credits</span>
                    </div>
                  </div>
                  
                  <motion.button
                    className="w-full lg:w-auto px-8 py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold text-lg hover:from-blue-500 hover:to-purple-500 shadow-lg shadow-blue-500/25 transition-all duration-300"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={openIntercomForSales}
                  >
                    Contact Sales
                  </motion.button>
                  
                  <p className="text-sm text-gray-400 mt-4">
                    Custom deployment & setup included
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-400 mb-6">
            Need something custom?
          </p>
          <motion.button
            className="px-8 py-3 border border-gray-600 text-gray-300 font-semibold rounded-xl hover:bg-gray-800 hover:border-gray-500 transition-all duration-300"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={openIntercomForSales}
          >
            Talk to Sales
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingSection;